import { useEffect, useState } from 'react';
import { apiDb } from './api_client';

/**
 * Integrytas Group - CSM: "may I activate/deactivate this one user?"
 *
 * Asks the SERVER (csm_can_deactivate_user), rather than re-deriving the answer
 * on the client. The list pages compute the same thing locally with
 * collectReportingSubtree because they already hold every row and would
 * otherwise fire one round-trip per row — but a single-record editor holds no
 * org context at all, and reconstructing a reporting chain from one row would
 * mean extra queries anyway. One RPC is cheaper and cannot drift from the rule
 * it is asking about.
 *
 * Denies until the server answers. A capability that flickers ON before the
 * real answer arrives is worse than one that appears a moment late: the user
 * gets an enabled control they may not actually have.
 */
export function useCanDeactivateUser(userId: number | string | null): boolean {
  const [canDeactivate, setCanDeactivate] = useState(false);

  useEffect(() => {
    // Child-editor registries carry the pk as `string | number`, so coerce here
    // rather than at each call site. A pk that is not a number is not a user id.
    const targetId = userId == null ? NaN : Number(userId);
    if (!Number.isFinite(targetId)) { setCanDeactivate(false); return; }

    let cancelled = false;
    void (async () => {
      const { data, error } = await apiDb
        .rpc<boolean>('csm_can_deactivate_user', { p_target_user_id: targetId });
      if (cancelled) return;
      // Fail CLOSED. An unreachable gateway is not permission — and the write
      // would be refused anyway, so offering the control could only mislead.
      setCanDeactivate(!error && data === true);
    })();

    return () => { cancelled = true; };
  }, [userId]);

  return canDeactivate;
}
